import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { User, MapPin, CreditCard, ChevronLeft, Save, X, Loader2 } from "lucide-react";

import { api } from "../../api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useLanguage } from "../../contexts/LanguageContext";
import { navigate, usePathname } from "../../lib/router";
import type { Client } from "../../types";
import { clientDetailsLabels } from "./labels";

export default function EditClientPage() {
  const { lang } = useLanguage();
  const t = clientDetailsLabels[lang].editPage;
  const path = usePathname();
  const clientId = useMemo(() => {
    const match = path.match(/\/app\/clients\/([^/]+)\/edit/);
    const val = match ? match[1] : null;
    if (val === "undefined" || val === "null") return null;
    return val;
  }, [path]);

  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [gender, setGender] = useState("");
  const [cpf, setCpf] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [street, setStreet] = useState("");
  const [addressNumber, setAddressNumber] = useState("");
  const [complement, setComplement] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [insurance, setInsurance] = useState("");
  const [plan, setPlan] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [emergencyContact, setEmergencyContact] = useState("");
  const [emergencyPhone, setEmergencyPhone] = useState("");

  const resetForm = useCallback(() => {
    setName(""); setBirthDate(""); setGender(""); setCpf(""); setPhone(""); setEmail("");
    setZip(""); setStreet(""); setAddressNumber(""); setComplement(""); setNeighborhood("");
    setCity(""); setState(""); setInsurance(""); setPlan(""); setCardNumber("");
    setEmergencyContact(""); setEmergencyPhone("");
  }, []);

  const populateFromClient = useCallback((data: Client) => {
    setName(data.name);
    setBirthDate(data.birth_date ? data.birth_date.split("T")[0] : "");
    setGender(data.gender || "");
    setCpf(data.cpf || "");
    setPhone(data.phone || "");
    setEmail(data.email || "");
    setZip(data.zip || "");
    setStreet(data.street || "");
    setAddressNumber(data.address_number || "");
    setComplement(data.complement || "");
    setNeighborhood(data.neighborhood || "");
    setCity(data.city || "");
    setState(data.state || "");
    setInsurance(data.insurance || "");
    setPlan(data.plan || "");
    setCardNumber(data.card_number || "");
    setEmergencyContact(data.emergency_contact || "");
    setEmergencyPhone(data.emergency_phone || "");
  }, []);

  useEffect(() => {
    resetForm();
    setClient(null);
  }, [clientId, resetForm]);

  useEffect(() => {
    if (!clientId) { setLoading(false); return; }
    let isMounted = true;
    (async () => {
      setLoading(true);
      const res = await api.getClient(clientId);
      if (!isMounted) return;
      if (res.ok) {
        setClient(res.data);
        populateFromClient(res.data);
      } else {
        toast.error(res.error);
        setClient(null);
      }
      setLoading(false);
    })();
    return () => { isMounted = false; };
  }, [clientId, populateFromClient]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!clientId) return;
    setSaving(true);
    const res = await api.updateClient(clientId, {
      name: name.trim(), birth_date: birthDate || null, gender: gender.trim() || null,
      cpf: cpf.trim() || null, phone: phone.trim() || null, email: email.trim() || null,
      zip: zip.trim() || null, street: street.trim() || null, address_number: addressNumber.trim() || null,
      complement: complement.trim() || null, neighborhood: neighborhood.trim() || null,
      city: city.trim() || null, state: state.trim() || null, insurance: insurance.trim() || null,
      plan: plan.trim() || null, card_number: cardNumber.trim() || null,
      emergency_contact: emergencyContact.trim() || null, emergency_phone: emergencyPhone.trim() || null,
    });
    setSaving(false);
    if (!res.ok) { toast.error(res.error); return; }
    navigate(`/app/clients/${clientId}`);
  };

  const goBack = () => clientId ? navigate(`/app/clients/${clientId}`) : navigate("/app/clients");

  const SectionTitle = ({ icon: Icon, title }: { icon: any, title: string }) => (
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600">
        <Icon className="w-5 h-5" />
      </div>
      <h2 className="text-lg font-bold text-neutral-800 tracking-tight">{title}</h2>
    </div>
  );

  const LabelStyled = ({ children }: { children: React.ReactNode }) => (
    <Label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1.5 block pl-1">
      {children}
    </Label>
  );

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-8 p-6 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={goBack}
            className="rounded-2xl hover:bg-neutral-100 h-11 w-11 transition-all active:scale-90"
          >
            <ChevronLeft className="h-6 w-6 text-neutral-600" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-800 tracking-tight">{t.title}</h1>
            <p className="text-xs font-medium text-neutral-400 uppercase tracking-widest">{client?.name}</p>
          </div>
        </div>
      </div>

      {!clientId || loading || !client ? (
        <div className="rounded-3xl border border-neutral-100 bg-white p-12 text-center">
          {loading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
              <p className="text-sm font-bold text-neutral-400 uppercase tracking-widest">{t.loading}</p>
            </div>
          ) : (
            <p className="text-sm font-bold text-neutral-400 uppercase tracking-widest">{!clientId ? t.invalid : t.notFound}</p>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Section */}
          <div className="rounded-3xl border border-neutral-100 bg-white p-8 shadow-sm">
            <SectionTitle icon={User} title={t.sections.personal.title} />
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-1">
                  <LabelStyled>{t.sections.personal.name}</LabelStyled>
                  <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
                </div>
                <div className="space-y-1">
                  <LabelStyled>{t.sections.personal.birthDate}</LabelStyled>
                  <Input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div className="space-y-1">
                  <LabelStyled>{t.sections.personal.gender}</LabelStyled>
                  <Input value={gender} onChange={(e) => setGender(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
                </div>
                <div className="space-y-1">
                  <LabelStyled>{t.sections.personal.cpf}</LabelStyled>
                  <Input value={cpf} onChange={(e) => setCpf(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
                </div>
                <div className="space-y-1">
                  <LabelStyled>{t.sections.personal.phone}</LabelStyled>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
                </div>
              </div>
              <div className="space-y-1">
                <LabelStyled>{t.sections.personal.email}</LabelStyled>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-1">
                  <LabelStyled>{t.sections.personal.emergencyContact}</LabelStyled>
                  <Input value={emergencyContact} onChange={(e) => setEmergencyContact(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
                </div>
                <div className="space-y-1">
                  <LabelStyled>{t.sections.personal.emergencyPhone}</LabelStyled>
                  <Input value={emergencyPhone} onChange={(e) => setEmergencyPhone(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
                </div>
              </div>
            </div>
          </div>

          {/* Address Section */}
          <div className="rounded-3xl border border-neutral-100 bg-white p-8 shadow-sm">
            <SectionTitle icon={MapPin} title={t.sections.address.title} />
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-7">
              <div className="sm:col-span-2 space-y-1">
                <LabelStyled>{t.sections.address.zip}</LabelStyled>
                <Input value={zip} onChange={(e) => setZip(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
              </div>
              <div className="sm:col-span-3 space-y-1">
                <LabelStyled>{t.sections.address.street}</LabelStyled>
                <Input value={street} onChange={(e) => setStreet(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <LabelStyled>{t.sections.address.addressNumber}</LabelStyled>
                <Input value={addressNumber} onChange={(e) => setAddressNumber(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <LabelStyled>{t.sections.address.complement}</LabelStyled>
                <Input value={complement} onChange={(e) => setComplement(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <LabelStyled>{t.sections.address.neighborhood}</LabelStyled>
                <Input value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <LabelStyled>{t.sections.address.city}</LabelStyled>
                <Input value={city} onChange={(e) => setCity(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
              </div>
              <div className="space-y-1">
                <LabelStyled>{t.sections.address.state}</LabelStyled>
                <Input value={state} onChange={(e) => setState(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
              </div>
            </div>
          </div>

          {/* Insurance Section */}
          <div className="rounded-3xl border border-neutral-100 bg-white p-8 shadow-sm">
            <SectionTitle icon={CreditCard} title={t.sections.insurance.title} />
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div className="space-y-1">
                <LabelStyled>{t.sections.insurance.insurance}</LabelStyled>
                <Input value={insurance} onChange={(e) => setInsurance(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
              </div>
              <div className="space-y-1">
                <LabelStyled>{t.sections.insurance.plan}</LabelStyled>
                <Input value={plan} onChange={(e) => setPlan(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
              </div>
              <div className="space-y-1">
                <LabelStyled>{t.sections.insurance.cardNumber}</LabelStyled>
                <Input value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} className="rounded-xl border-neutral-100 focus-visible:ring-emerald-500/20" />
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="fixed bottom-0 left-0 right-0 p-6 bg-white/80 backdrop-blur-lg border-t border-neutral-100 z-10">
            <div className="mx-auto max-w-4xl flex items-center justify-end gap-4">
              <Button 
                type="button" 
                variant="ghost" 
                onClick={goBack}
                className="h-12 px-8 font-bold text-neutral-400 rounded-2xl hover:bg-neutral-50 transition-all"
              >
                <X className="w-4 h-4 mr-2" />
                {t.actions.cancel}
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="h-12 px-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl transition-all active:scale-[0.98] disabled:bg-neutral-100 disabled:text-neutral-400"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {saving ? t.actions.saving : t.actions.save}
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
